import React from 'react';
import { useTranslation } from 'react-i18next';

import { Icon, Text, Skeleton, LockVeil } from '../../../../components';
import { ACQ, CHANNEL_GRAD_RED, CHANNEL_GRAD_GOLD } from '../../../../theme/tokens';
import { useAcquisitionChannelGate } from '../../../../hooks/useAcquisitionChannelGate';
import type { LumioChannel, Budgets as BudgetsData, ChannelState } from '../../../../mock-data/types';
import {
  Card,
  TwoCol,
  Col,
  SecTitle,
  SecSpark,
  ChannelsGrid,
  Channel,
  ChMain,
  ChTop,
  ChIcon,
  ChName,
  ChBar,
  ChBarFill,
  ChFoot,
  ChSpent,
  Dim,
  ChCpa,
  ChBottom,
  ChStatus,
  Chev,
  BudgetCard,
  BudgetSubs,
  BudgetSub,
  BudgetIcon,
  BudgetVal,
  BudgetLbl,
  BudgetCta,
  Disclaimer,
  DisclaimerIcon,
  DisclaimerText,
} from './ChannelsBudgets.styles';
import BudgetLimitsModal from '../AdBudgetLimit/BudgetLimitsModal';

const CTA_SPARK = '/assets/acq/cta-sparkle-icon.svg';

// "Tuesday, June 16, 2026": the previous calendar day, which the figures cover.
const prettyYesterday = (): string => {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
};

type ChannelTone = { gradient: string; border: string; status: string; bar: string };

// Exhausted = red radial + red accents; nearing-budget = gold; healthy = no glow + cyan bar.
const toneFor = (state: ChannelState): ChannelTone => {
  if (state === 'exhausted') {
    return { gradient: CHANNEL_GRAD_RED, border: ACQ.redBorder, status: ACQ.red, bar: ACQ.red };
  }
  if (state === 'nearing-budget') {
    return { gradient: CHANNEL_GRAD_GOLD, border: ACQ.goldBorder, status: ACQ.gold, bar: ACQ.gold };
  }
  return { gradient: 'none', border: 'transparent', status: ACQ.t300, bar: ACQ.channelOk };
};

// Gated channels render a fully neutral grey tone (no glow, no colour-coded bar or
// border) so nothing grabs attention behind the frosted lock — only the live Web card
// keeps its health colour coding. See `config/acquisitionChannels`.
const LOCKED_TONE: ChannelTone = { gradient: 'none', border: 'transparent', status: ACQ.t500, bar: ACQ.t500 };

export type ChannelsBudgetsProps = {
  channels: LumioChannel[];
  budgets: BudgetsData;
  /** Open a channel — the id is read from `event.currentTarget.dataset.id`. */
  onOpenChannel: (event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => void;
  onOpenBudgetLimits: () => void;
  onCloseBudgetLimits: () => void;
  openBudgetDialog: boolean;
  /** While true, the spend/budget figures + channel bars show placeholders (the endpoint is loading). */
  loading?: boolean;
  /** While true, the Budgets tiles (Remaining Budget / Daily Limit) skeleton while the acquisition-budget API loads. */
  budgetsLoading?: boolean;
};

/** "Lumio Channels" grid + "Budgets" card, wrapped in one container (source 6466-33091). */
export const ChannelsBudgets = ({
  channels,
  budgets,
  onOpenChannel,
  onOpenBudgetLimits,
  onCloseBudgetLimits,
  openBudgetDialog,
  loading = false,
  budgetsLoading = false,
}: ChannelsBudgetsProps): React.ReactElement => {
  const { t } = useTranslation();
  // Reactive gate: re-renders the grid when a channel is unlocked/locked at runtime.
  const { isEnabled } = useAcquisitionChannelGate();
  // Channel names and budget-tile labels are a fixed UI vocabulary (the figures are
  // the per-brand data); they arrive as dynamic props from the data source, so
  // translate them at the render site over the known set (unknown values render
  // as-is).
  const channelName = (name: string): string => {
    const labels: Record<string, string> = {
      'Web Networks': t('Web Networks'),
      'App Networks': t('App Networks'),
      'Crypto Sites': t('Crypto Sites'),
      'Short Videos': t('Short Videos'),
    };
    return labels[name] ?? name;
  };
  const budgetLabel = (label: string): string => {
    const labels: Record<string, string> = {
      'Remaining Budget': t('Remaining Budget'),
      'Daily Limit': t('Daily Limit'),
    };
    return labels[label] ?? label;
  };
  // Channel identity (icon + name) is known while the metrics load, so only the
  // figures + bars stand in. The bars grow in once the data arrives: `revealed`
  // flips false→true a frame after `loading` clears, driving the fill transition
  // (a load-keyed `useMountAnimation`).
  const [revealed, setRevealed] = React.useState(false);
  React.useEffect(() => {
    if (loading) {
      setRevealed(false);
      return;
    }
    const id = requestAnimationFrame(() => setRevealed(true));
    return () => cancelAnimationFrame(id);
  }, [loading]);

  // Computed once per mount: the figures reflect the prior day, so the note
  // names that date rather than "yesterday" in the abstract.
  const reportDate = React.useMemo(prettyYesterday, []);

  return (
    <Card>
      <TwoCol>
        <Col>
          <SecTitle>
            <SecSpark src={CTA_SPARK} alt="" />
            <Text text="Lumio Channels" tag="span" />
          </SecTitle>
          <ChannelsGrid>
            {channels.map((channel, index) => {
              // Gated channels (release feature flag) are inert and frosted under a
              // padlock instead of drilling into their detail page, and render a neutral
              // grey tone so no colour coding grabs attention behind the frost.
              const locked = !isEnabled(channel.id);
              const tone = locked ? LOCKED_TONE : toneFor(channel.state);
              return (
                <Channel
                  key={channel.id}
                  $gradient={tone.gradient}
                  $border={tone.border}
                  $locked={locked}
                  data-id={channel.id}
                  role={locked ? undefined : 'button'}
                  tabIndex={locked ? -1 : 0}
                  aria-disabled={locked || undefined}
                  onClick={locked ? undefined : onOpenChannel}
                  onKeyDown={
                    locked
                      ? undefined
                      : (event) => {
                          if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            onOpenChannel(event);
                          }
                        }
                  }
                >
                  <ChMain>
                    <ChTop>
                      <ChIcon>
                        <Icon name={channel.icon} size={22} color={ACQ.t300} />
                      </ChIcon>
                      <ChName text={channelName(channel.name)} tag="span" />
                    </ChTop>
                    <ChBar>
                      {loading ? (
                        <Skeleton block width="100%" height="100%" radius={4} />
                      ) : (
                        <ChBarFill $color={tone.bar} $pct={channel.progress} $mounted={revealed} $index={index} />
                      )}
                    </ChBar>
                    <ChFoot>
                      {loading ? (
                        <Skeleton width={72} height={14} radius={6} />
                      ) : (
                        <ChSpent>
                          <Text text={`$${channel.spent}`} tag="span" />
                          <Dim text={`/$${channel.budget}`} tag="span" />
                        </ChSpent>
                      )}
                      {loading ? <Skeleton width={46} height={14} radius={6} /> : <ChCpa text={channel.cpa} tag="span" />}
                    </ChFoot>
                  </ChMain>
                  <ChBottom>
                    {loading ? (
                      <Skeleton width={72} height={16} radius={8} />
                    ) : channel.badge ? (
                      <ChStatus $color={tone.status} text={channel.badge} tag="span" />
                    ) : (
                      <span />
                    )}
                    <Chev>
                      <Icon name="arrow-right-circle" size={24} color={ACQ.t50} />
                    </Chev>
                  </ChBottom>
                  {/* Match this card's header (ChIcon 22 · ChName 14px · gap 10 · padding 10,
                      +5px top from ChTop) and put the padlock where the chevron sits (bottom-right). */}
                  {locked ? (
                    <LockVeil name={channelName(channel.name)} icon={channel.icon} iconSize={22} fontSize={14} headerGap={10} pad="15px 10px 10px" lockCorner="bottom-right" />
                  ) : null}
                </Channel>
              );
            })}
          </ChannelsGrid>
        </Col>

        <Col>
          <SecTitle>
            <Text text="Budgets" tag="span" />
          </SecTitle>
          <BudgetCard>
            <BudgetSubs>
              {budgets.items.map((item) => (
                <BudgetSub key={item.id}>
                  <BudgetIcon>
                    <Icon name={item.icon} size={item.icon === 'wallet' ? 24 : 22} color={ACQ.t300} />
                  </BudgetIcon>
                  {loading || budgetsLoading ? (
                    <Skeleton width={66} height={20} radius={6} />
                  ) : (
                    <BudgetVal text={item.value} tag="span" />
                  )}
                  <BudgetLbl text={budgetLabel(item.label)} tag="span" />
                </BudgetSub>
              ))}
            </BudgetSubs>
            <BudgetCta variant="outline" icon="edit" label={t('Lumio Budget and Limits')} onClick={onOpenBudgetLimits} fullWidth />
          </BudgetCard>

          <BudgetLimitsModal isOpen={openBudgetDialog} onClose={onCloseBudgetLimits} />
        </Col>
      </TwoCol>

      <Disclaimer>
        <DisclaimerIcon>
          <Icon name="info" size={14} color={ACQ.t400} />
        </DisclaimerIcon>
        <DisclaimerText
          tag="span"
          text="Data shown is from {{date}}. Any limit and budget adjustments take effect the following day."
          placeholders={{ date: reportDate }}
        />
      </Disclaimer>
    </Card>
  );
};

export default ChannelsBudgets;
